import { get } from 'lodash';
import styles from './GA2RFValuesTable.module.css';

// Default data formats for Az/El parameters
const defaultAzElTableDataFormat = [
  {
    id: 'az-electrical',
    uiName: 'Az - Electrical (HBF Only)',
    data: {} // {du: 123, su1: 123...}
  },
  {
    id: 'el-electrical',
    uiName: 'El- Electrical (HBF Only)',
    data: {}
  }
];

// Default data formats for Link parameters
const defaultLinkTableDataFormat = [
  {
    id: 'system-gain-db',
    uiName: 'System Gain (dB)',
    data: {} // {su1: 123/123, su2: 123/123...}
  },
  {
    id: 'antenna-gain-db',
    uiName: 'Antenna Gain (dB)',
    data: {}
  },
  {
    id: 'electronic-gain-db',
    uiName: 'Electronic Gain (dB)',
    data: {}
  },
  {
    id: 'rx-gain-db',
    uiName: 'Rx Gain (dB)',
    data: {}
  },
  {
    id: 'tx-gain-db',
    uiName: 'Tx Gain (dB)',
    data: {}
  },
  {
    id: 'tx-eirp-dbm',
    uiName: 'Tx EIRP (dBm)',
    data: {}
  }
];

export function azElDataParser(deviceMembers = []) {
  // deviceMembers is an array of objects from device twin properties.reported, [du, su1, su2]
  const data = JSON.parse(JSON.stringify(defaultAzElTableDataFormat));
  for (let obj of data) {
    for (let i = 0; i < deviceMembers.length; i++) {
      const deviceMember = deviceMembers[i];
      const isSU = deviceMember?.type?.startsWith('SU');
      const dataHeader = i === 0 ? 'du' : `su${i}`;

      // TODO: Az and El data paths for SUs are still being defined
      const electricalAzEl = isSU
        ? { azimuth: 'N/A', elevation: 'N/A' }
        : get(deviceMember, 'az-el-info.downlink.vpol', { azimuth: 'N/A', elevation: 'N/A' });

      switch (obj.id) {
        case 'az-electrical':
          obj.data[dataHeader] = electricalAzEl.azimuth;
          break;
        case 'el-electrical':
          obj.data[dataHeader] = electricalAzEl.elevation;
          break;
        default:
          break;
      }
    }
  }

  return data;
}

export function linkParameterDataParser(deviceMembers = [], isUplink = true) {
  const data = JSON.parse(JSON.stringify(defaultLinkTableDataFormat));
  for (let obj of data) {
    for (let i = 0; i < deviceMembers.length; i++) {
      const su = deviceMembers[i];
      const dataHeader = su.type.toLowerCase();
      const hpolVal = get(su, `rfPath.normal-op-info.${isUplink ? 'uplink' : 'downlink'}.hpol.${obj.id}`, 'N/A');
      const vpolVal = get(su, `rfPath.normal-op-info.${isUplink ? 'uplink' : 'downlink'}.vpol.${obj.id}`, 'N/A');
      obj.data[dataHeader] = `${hpolVal} / ${vpolVal}`;
    }
  }
  return data;
}

const createSuCol = (sectorNum, isLastCol, headerName, maxColWidth) => {
  const column = {
    id: `su${sectorNum}`,
    sortable: false,
    accessor: (parameter) => parameter.data[`su${sectorNum}`],
    Header: headerName,
    Cell: (row) => (
      <div className={styles.cellText} title={row.value ?? 'N/A'}>
        {row.value ?? 'N/A'}
      </div>
    ),
    className: styles.cell
  };

  if (!isLastCol) {
    column.maxWidth = maxColWidth;
  }

  return column;
};

export function azElColumns(members = []) {
  const numberOfSUs = members.length;
  const suColumns = [];
  for (let i = 0; i < numberOfSUs; i++) {
    const member = members[i];
    const sectorNum = member.type.slice(2);
    const isLastCol = i === numberOfSUs - 1;
    const headerName = `Sector ${sectorNum} SU`;
    const maxColWidth = 125;
    const suCol = createSuCol(sectorNum, isLastCol, headerName, maxColWidth);
    suColumns.push(suCol);
  }
  return [
    {
      id: 'parameter',
      sortable: false,
      maxWidth: 220,
      accessor: (parameter) => parameter.uiName,
      Header: 'Parameter (Antenna)',
      Cell: (row) => {
        return (
          <div className={styles.cellText} title={row.value}>
            {row.value}
          </div>
        );
      },
      className: styles.cell
    },
    {
      id: 'du',
      sortable: false,
      ...(numberOfSUs !== 0 && { maxWidth: 100 }),
      accessor: (parameter) => parameter.data.du,
      Header: 'gNB -> DU',
      Cell: (row) => {
        return (
          <div className={styles.cellText} title={row.value ?? 'N/A'}>
            {row.value ?? 'N/A'}
          </div>
        );
      },
      className: styles.cell
    },
    ...suColumns
  ].filter(Boolean);
}

export function linkParameterColumns(members = [], isUplink = true) {
  const numberOfSUs = members.length;
  const suColumns = [];
  for (let i = 0; i < numberOfSUs; i++) {
    const member = members[i];
    const sectorNum = member.type.slice(2);
    const isLastCol = i === numberOfSUs - 1;
    const headerName = `Sector ${sectorNum} (H/V)`;
    const maxColWidth = 125;
    const suCol = createSuCol(sectorNum, isLastCol, headerName, maxColWidth);
    suColumns.push(suCol);
  }
  return [
    {
      id: 'parameter',
      sortable: false,
      ...(numberOfSUs !== 0 && { maxWidth: 180 }),
      accessor: (parameter) => parameter.uiName,
      Header: `Parameter (${isUplink ? 'Uplink' : 'Downlink'})`,
      Cell: (row) => {
        return (
          <div className={styles.cellText} title={row.value}>
            {row.value}
          </div>
        );
      },
      className: styles.cell
    },
    ...suColumns
  ].filter(Boolean);
}
